// @flow
import actionCreators from "@modules/reporting/actionCreator";
import type { Action } from "redux-action";
import { push } from "connected-react-router";
import { route } from "@routes/helper";
import { all, call, put, takeEvery } from "redux-saga/effects";
import http from "@http";
import {
  GetAbsenceReasonTrendsDefaultReports,
  GetAbsenceReasonTrendsMyReports,
  GetAbsenceReasonTrendsReportData,
  GetAbsenceProfile,
  GetAbsenceReasonTrendsReportSchema,
  CreateAbsenceReasonTrendsReport,
  UpdateAbsenceReasonTrendsReport,
  DeleteAbsenceReasonTrendsReport,
  ExportAbsenceReasonTrendsReport,
  ExportAbsenceReasonTrendsReportSuccess,
} from "@modules/reporting/constants/actions";
import type {
  AbsenceReasonReport,
  AbsenceReasonReportFilter,
  AbsenceProfile,
} from "@modules/reporting/reducer/trends/absence-reason/types";
import { sendSuccessMessage } from "@modules/app/actionCreator";
import { downloadCsvFile } from "@sagas/utils";
import { parseSchema } from "@utils/schema";

type TrendsReportsRequest = $Exact<{
  clientId: string,
}>;

export function* onGetAbsenceReasonTrendsDefaultReports({
  payload,
}: Action<TrendsReportsRequest>): Generator<*, void, { data: Array<AbsenceReasonReport> }> {
  try {
    if (!payload || !payload.clientId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v1/trends/client/${payload.clientId}/trends/absence-reason/default-reports`,
    );

    yield put(actionCreators.GetAbsenceReasonTrendsDefaultReportsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetAbsenceReasonTrendsDefaultReportsError(e.response));
  }
}

export function* onGetAbsenceReasonTrendsMyReports({
  payload,
}: Action<TrendsReportsRequest>): Generator<*, void, { data: Array<AbsenceReasonReport> }> {
  try {
    if (!payload || !payload.clientId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v1/trends/client/${payload.clientId}/trends/absence-reason/my-saved-reports`,
    );

    yield put(actionCreators.GetAbsenceReasonTrendsMyReportsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetAbsenceReasonTrendsMyReportsError(e.response));
  }
}

type TrendsReportDataRequest = $Exact<{
  ...TrendsReportsRequest,
  report: AbsenceReasonReport,
  noRedirect?: boolean,
}>;

export function* onGetAbsenceReasonTrendsReportData({
  payload,
}: Action<TrendsReportDataRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.report) {
      throw Error("Invalid payload provided");
    }

    const [reportResponse, chartsResponse] = yield all([
      call(
        http.post,
        `/api/v1/trends/client/${payload.clientId}/trends/absence-reason`,
        payload.report.filters,
      ),
      call(
        http.post,
        `/api/v1/trends/client/${payload.clientId}/trends/absence-reason/charts`,
        payload.report.filters,
      ),
    ]);

    yield put(
      actionCreators.GetAbsenceReasonTrendsReportDataSuccess({
        details: payload.report,
        reportData: {
          details: reportResponse.data.content,
          top5ReasonsByPeriod: chartsResponse.data.top5ReasonsByPeriod,
          top5Reasons: chartsResponse.data.top5Reasons,
        },
      }),
    );

    if (!payload.noRedirect) {
      yield put(
        push(route("REPORTING_TRENDS_ABSENCE_REASON_REPORT", { clientId: payload.clientId })),
      );
    }
  } catch (e) {
    yield put(actionCreators.GetAbsenceReasonTrendsReportDataError(e.response));
  }
}

export function* onGetAbsenceReasonTrendsReportSchema({
  payload,
}: Action<TrendsReportsRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId) {
      throw Error("Invalid payload provided");
    }
    const schema = yield parseSchema("AbsenceReasonTrendsReportFiltersDTO", {
      clientId: payload.clientId,
    });

    yield put(actionCreators.GetAbsenceReasonTrendsReportSchemaSuccess(schema));
  } catch (e) {
    yield put(actionCreators.GetAbsenceReasonTrendsReportSchemaError(e.response));
  }
}

type CreateAbsenceReasonTrendsReportRequest = $Exact<{
  clientId: number,
  filters: Object,
  name: string,
}>;

export function* onCreateAbsenceReasonTrendsReport({
  payload,
}: Action<CreateAbsenceReasonTrendsReportRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.filters || !payload.name) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.post,
      `/api/v1/trends/client/${payload.clientId}/trends/absence-reason/my-saved-reports`,
      {
        name: payload.name,
        filters: payload.filters,
      },
    );

    yield all([
      put(actionCreators.CreateAbsenceReasonTrendsReportSuccess(response.data)),
      put(actionCreators.ToggleSaveReportModal({ visible: false })),
      put(actionCreators.GetAbsenceReasonTrendsMyReports({ clientId: payload.clientId })),
      put(sendSuccessMessage("REPORT_CREATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.CreateAbsenceReasonTrendsReportError(e.response));
  }
}

type UpdateAbsenceReasonTrendsReportRequest = $Exact<{
  clientId: number,
  reportId: number,
  data: Object,
  shouldFetchReportData?: boolean,
}>;

export function* onUpdateAbsenceReasonTrendsReport({
  payload,
}: Action<UpdateAbsenceReasonTrendsReportRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.reportId || !payload.data) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.put,
      `/api/v1/trends/client/${payload.clientId}/trends/absence-reason/my-saved-reports/${payload.reportId}`,
      payload.data,
    );

    const additionalActions = payload.shouldFetchReportData
      ? [
          put(
            actionCreators.GetAbsenceReasonTrendsReportData({
              clientId: payload.clientId,
              report: response.data,
              noRedirect: true,
            }),
          ),
        ]
      : [];

    yield all([
      put(actionCreators.UpdateAbsenceReasonTrendsReportSuccess(response.data)),
      put(actionCreators.ToggleSaveReportModal({ visible: false })),
      put(actionCreators.GetAbsenceReasonTrendsMyReports({ clientId: payload.clientId })),
      put(sendSuccessMessage("REPORT_UPDATED")),
      ...additionalActions,
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateAbsenceReasonTrendsReportError(e.response));
  }
}

type DeleteAbsenceReasonTrendsReportRequest = $Exact<{
  clientId: number,
  reportId: number,
  successMessage: string,
}>;

export function* onDeleteAbsenceReasonTrendsReport({
  payload,
}: Action<DeleteAbsenceReasonTrendsReportRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.reportId || !payload.successMessage) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.delete,
      `/api/v1/trends/client/${payload.clientId}/trends/absence-reason/my-saved-reports/${payload.reportId}`,
    );

    yield all([
      put(actionCreators.DeleteAbsenceReasonTrendsReportSuccess()),
      put(actionCreators.GetAbsenceReasonTrendsMyReports({ clientId: payload.clientId })),
      put(sendSuccessMessage(payload.successMessage)),
    ]);
  } catch (e) {
    yield put(actionCreators.DeleteAbsenceReasonTrendsReportError(e.response));
  }
}

type TrendsAbsenceProfileRequest = $Exact<{
  ...TrendsReportsRequest,
  absenceReasonId: number,
  filters: AbsenceReasonReportFilter,
}>;

export function* onGetAbsenceProfile({
  payload,
}: Action<TrendsAbsenceProfileRequest>): Generator<*, void, { data: AbsenceProfile }> {
  try {
    if (!payload || !payload.clientId || !payload.absenceReasonId || !payload.filters) {
      throw Error("Invalid payload provider");
    }

    const response = yield call(
      http.post,
      `/api/v1/trends/client/${payload.clientId}/trends/absence-reason/${payload.absenceReasonId}/absence-profile`,
      payload.filters,
    );

    yield put(actionCreators.GetAbsenceProfileSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetAbsenceProfileError(e.response));
  }
}

type ExportAbsenceReasonTrendsRequest = $Exact<{
  ...TrendsReportsRequest,
  filters: AbsenceReasonReportFilter,
}>;

export function* onExportAbsenceReasonTrendsReport({
  payload,
}: Action<ExportAbsenceReasonTrendsRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.filters) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.post,
      `/api/v1/trends/client/${payload.clientId}/trends/absence-reason/export`,
      payload.filters,
      {
        headers: {
          Accept: "text/csv",
        },
      },
    );

    yield put(actionCreators.ExportAbsenceReasonTrendsReportSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.ExportAbsenceReasonTrendsReportError(e.response));
  }
}

export function* onExportAbsenceReasonTrendsReportSuccess({
  payload,
}: Action<any>): Generator<*, void, any> {
  yield downloadCsvFile(payload, "absence-reason-trends-report.csv");
}

export function* AbsenceReasonSaga(): Generator<*, void, void> {
  yield all([
    takeEvery(GetAbsenceReasonTrendsDefaultReports, onGetAbsenceReasonTrendsDefaultReports),
    takeEvery(GetAbsenceReasonTrendsMyReports, onGetAbsenceReasonTrendsMyReports),
    takeEvery(GetAbsenceReasonTrendsReportData, onGetAbsenceReasonTrendsReportData),
    takeEvery(GetAbsenceProfile, onGetAbsenceProfile),
    takeEvery(GetAbsenceReasonTrendsReportSchema, onGetAbsenceReasonTrendsReportSchema),
    takeEvery(CreateAbsenceReasonTrendsReport, onCreateAbsenceReasonTrendsReport),
    takeEvery(UpdateAbsenceReasonTrendsReport, onUpdateAbsenceReasonTrendsReport),
    takeEvery(DeleteAbsenceReasonTrendsReport, onDeleteAbsenceReasonTrendsReport),
    takeEvery(ExportAbsenceReasonTrendsReport, onExportAbsenceReasonTrendsReport),
    takeEvery(ExportAbsenceReasonTrendsReportSuccess, onExportAbsenceReasonTrendsReportSuccess),
  ]);
}

export default AbsenceReasonSaga;
